import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { resolvePostLoginPath } from '../auth/redirects';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { LoadingState } from '../components/LoadingState';
import { PublicOnboardingShell } from '../components/PublicOnboardingShell';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

function getLoginErrorMessage(err, t) {
  const data = err?.response?.data;

  if (data?.errors?.email?.[0]) {
    return data.errors.email[0];
  }

  if (data?.message) {
    return data.message;
  }

  return t('auth:login_failed');
}

function getChallengeErrorMessage(err, t) {
  return err?.response?.data?.message || t('auth:invalid_code');
}

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { login, completeTwoFactorLogin, isAuthenticated, isLoading, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [challengeCode, setChallengeCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const from = location.state?.from?.pathname;
  const successMessage = location.state?.message;

  useEffect(() => {
    if (!isLoading && isAuthenticated && session) {
      navigate(resolvePostLoginPath(session, from), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from, session]);

  if (isLoading) {
    return <LoadingState fullScreen />;
  }

  if (isAuthenticated && session) {
    return <Navigate to={resolvePostLoginPath(session, from)} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await login(email, password);

      if (result.requires_two_factor) {
        setChallenge(result);
        return;
      }

      navigate(resolvePostLoginPath(result, from), {
        replace: true,
        state: successMessage ? { message: successMessage } : undefined,
      });
    } catch (err) {
      setError(getLoginErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChallengeSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const payload = {
        two_factor_challenge_token: challenge.two_factor_challenge_token,
        device_name: 'mafert-system-web',
      };

      if (useRecoveryCode) {
        payload.recovery_code = challengeCode;
      } else {
        payload.code = challengeCode;
      }

      const sessionData = await completeTwoFactorLogin(payload);
      navigate(resolvePostLoginPath(sessionData, from), { replace: true });
    } catch (err) {
      setError(getChallengeErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const resetToLogin = () => {
    setChallenge(null);
    setChallengeCode('');
    setUseRecoveryCode(false);
    setError(null);
  };

  return (
    <PublicOnboardingShell
      maxWidth="sm"
      title={challenge ? t('auth:two_factor_title') : t('auth:login_title')}
      subtitle={
        challenge
          ? undefined
          : t('auth:login_subtitle')
      }
      footer={(
        <Typography component="span" sx={{ fontSize: 12, color: semantic.text.muted }}>
          {t('auth:no_account_yet')}{' '}
          <Box
            component={RouterLink}
            to="/register"
            sx={{ color: semantic.text.secondary, textDecoration: 'underline' }}
          >
            {t('auth:create_trial_account')}
          </Box>
        </Typography>
      )}
      testId="public-login-shell"
    >
      {challenge ? (
        <Typography
          sx={{
            mb: 2,
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            color: semantic.text.secondary,
          }}
        >
          {t('auth:signed_in_as')}{' '}
          <strong style={{ color: semantic.text.primary }}>{challenge.user_preview?.email}</strong>
        </Typography>
      ) : null}

      {successMessage && !challenge && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {challenge ? (
        <Box component="form" onSubmit={handleChallengeSubmit}>
          <Stack spacing={2}>
            <TextInput
              label={useRecoveryCode ? t('auth:recovery_code') : t('auth:auth_code')}
              value={challengeCode}
              onChange={(e) => setChallengeCode(e.target.value)}
              required
              autoComplete="one-time-code"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={useRecoveryCode}
                  onChange={(e) => {
                    setUseRecoveryCode(e.target.checked);
                    setChallengeCode('');
                  }}
                />
              )}
              label={t('auth:use_recovery_code')}
              sx={{ color: semantic.text.secondary }}
            />
            <Button type="submit" variant="Primary" fullWidth disabled={submitting || !challengeCode} loading={submitting}>
              {t('auth:verify')}
            </Button>
            <Button type="button" variant="Ghost" fullWidth onClick={resetToLogin}>
              {t('auth:back_to_login')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextInput
              label={t('auth:email')}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputProps={{ 'data-testid': 'login-email' }}
            />
            <TextInput
              label={t('auth:password')}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              inputProps={{ 'data-testid': 'login-password' }}
            />
            <Box sx={{ textAlign: 'right' }}>
              <Box
                component={RouterLink}
                to="/forgot-password"
                data-testid="login-forgot-password"
                sx={{ fontSize: 13, color: semantic.text.secondary, textDecoration: 'underline' }}
              >
                {t('auth:forgot_password_link')}
              </Box>
            </Box>
            <Button type="submit" variant="Primary" fullWidth disabled={submitting} loading={submitting}>
              {t('auth:login')}
            </Button>
          </Stack>
        </Box>
      )}
    </PublicOnboardingShell>
  );
}
